import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/es/table/interface";
import { App } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

import QueryTable from "@/components_v2/Tables/QueryTable";
import {
  useLazyGetAdminSurveyStatisticsQuery,
  useUpdateSurveyAssignmentMutation,
} from "@/redux/service.js";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.js";
import { useSurveyViewDownload } from "@/domains/survey-views/hooks/useSurveyViewDownload";
import {
  SurveyStatisticsFiltersState,
  SurveyStatisticsTableProps,
} from "../types";
import { SurveyAssignmentStatisticsAdminResponse } from "@/redux/survey/types";
import SurveyStatisticsFilters from "@/features/survey/management/components/SurveyStatisticsFilters";

const SurveyStatisticsTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  filtersCallback,
  ...props
}: SurveyStatisticsTableProps<SurveyAssignmentStatisticsAdminResponse>) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [updateSurveyAssignment] = useUpdateSurveyAssignmentMutation();
  const { downloadSurveyView, isDownloading } = useSurveyViewDownload();
  const [filters, setFilters] =
    useState<Partial<SurveyStatisticsFiltersState>>(defaultFilters);

  const handleChangeSubmissionRequired = (
    detailsId: number,
    newValue: boolean,
  ) => {
    updateSurveyAssignment({
      id: detailsId,
      data: { isSubmissionRequired: newValue },
    })
      .unwrap()
      .then(() =>
        notification.success({
          message: t("survey.labels.surveyDetailsUpdated"),
        }),
      );
  };

  const columns: Record<
    string,
    ColumnType<SurveyAssignmentStatisticsAdminResponse>
  > = useMemo(() => {
    return {
      name: {
        title: t("survey.labels.name"),
        key: "name",
        dataIndex: ["organization_name"],
        sorter: true,
        width: 400,
        ellipsis: true,
        render: (_, record) => {
          let navigateTo;
          let name;
          if (defaultFilters?.survey) {
            if (record?.organization) {
              navigateTo = `/reports/${defaultFilters.survey}/overview?organization=${record.organization.id}`;
              name = record.organization.name;
            } else if (record.user) {
              navigateTo = `/reports/${defaultFilters.survey}/overview?user=${record.user.id}`;
              name = record.user.fullName;
            } else {
              return;
            }
          }

          return (
            <Link
              style={{ textDecoration: "underline" }}
              to={navigateTo || "#"}
            >
              {name}
            </Link>
          );
        },
      },
      isSurveyStarted: {
        title: t("survey.labels.isSurveyStarted"),
        dataIndex: "isSurveyStarted",
        key: "isSurveyStarted",
        render: (_, record) =>
          record.isSurveyStarted ? (
            <CheckCircleOutlined style={{ color: "green" }} />
          ) : (
            <CloseCircleOutlined style={{ color: "red" }} />
          ),
      },
      answeredQuestionsCount: {
        title: t("survey.labels.answeredQuestions"),
        dataIndex: "answeredQuestionsCount",
        key: "answeredQuestionsCount",
      },
      attachedEvidenceCount: {
        title: t("survey.labels.attachedEvidence"),
        dataIndex: "answeredBlocksCount",
        key: "answeredBlocksCount",
      },
      isSubmissionRequired: {
        title: t("survey.labels.isSubmissionRequired"),
        dataIndex: "isSubmissionRequired",
        key: "isSubmissionRequired",
        render: (_, record) => {
          const handleClick = () =>
            handleChangeSubmissionRequired(
              record.id,
              !record.isSubmissionRequired,
            );
          return record.isSubmissionRequired ? (
            <CheckCircleOutlined
              onClick={handleClick}
              style={{ color: "green" }}
            />
          ) : (
            <CloseCircleOutlined
              onClick={handleClick}
              style={{ color: "red" }}
            />
          );
        },
      },
      isSubmitted: {
        title: t("survey.labels.isSubmitted"),
        dataIndex: "isSubmitted",
        key: "isSubmitted",
        render: (_, record) =>
          record.isSubmitted ? (
            <CheckCircleOutlined style={{ color: "green" }} />
          ) : (
            <CloseCircleOutlined style={{ color: "red" }} />
          ),
      },
      downloadReport: {
        title: t("survey.labels.downloadReport"),
        key: "report",
        render: (_, record) => {
          const websiteSurveyViewId = record.websiteSurveyViewId;
          if (!websiteSurveyViewId) return;

          return isDownloading(websiteSurveyViewId) ? (
            <BaseSpinner size="small" />
          ) : (
            <DownloadOutlined
              onClick={() =>
                downloadSurveyView(
                  websiteSurveyViewId,
                  `${record.organization}.pdf`,
                )
              }
              style={{ color: "grey", fontSize: "20px" }}
            />
          );
        },
      },
    };
  }, []);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) =>
          columns[
            columnKey
          ] as ColumnType<SurveyAssignmentStatisticsAdminResponse>,
      ),
    [columns, columnNames],
  );

  return (
    <div className="table-container-layout">
      <SurveyStatisticsFilters
        filterNames={filterNames}
        setFilters={(data) => {
          setFilters(data);
          if (filtersCallback) filtersCallback(data);
        }}
        defaultFilters={defaultFilters}
      />
      <QueryTable<SurveyAssignmentStatisticsAdminResponse>
        query={useLazyGetAdminSurveyStatisticsQuery}
        filters={filters}
        initialSorting={{
          field: "name",
          order: "ascend",
        }}
        columns={mappedColumns}
        {...props}
      />
    </div>
  );
};

export default SurveyStatisticsTable;
